import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase';

export interface Office {
  id: number;
  name: string;
  created_at?: string;
}

interface OfficeContextValue {
  offices: Office[];
  currentOfficeId: number | null;
  currentOffice: Office | null;
  loading: boolean;
  createOffice: (name: string) => Promise<Office | null>;
  renameOffice: (id: number, name: string) => Promise<Office | null>;
  deleteOffice: (id: number) => Promise<boolean>;
  switchOffice: (id: number) => void;
  refreshOffices: () => Promise<void>;
}

const OfficeContext = createContext<OfficeContextValue | undefined>(undefined);

const STORAGE_KEY = 'tapevault_current_office_id';

export function OfficeProvider({ children }: { children: ReactNode }) {
  const [offices, setOffices] = useState<Office[]>([]);
  const [currentOfficeId, setCurrentOfficeId] = useState<number | null>(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    const n = raw ? Number(raw) : NaN;
    return Number.isFinite(n) ? n : null;
  });
  const [loading, setLoading] = useState(true);

  // Load only the offices the current user is allowed to see:
  // admins see every office, regular users see the offices they've been
  // explicitly granted access to (via office_members).
  const refreshOffices = useCallback(async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id ?? null;
      if (!userId) {
        setOffices([]);
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', userId)
        .maybeSingle();
      const isAdmin = profile?.role === 'admin';

      if (isAdmin) {
        const { data, error } = await supabase
          .from('offices')
          .select('*')
          .order('id', { ascending: true });
        if (error) throw error;
        setOffices((data ?? []) as Office[]);
      } else {
        const { data: members, error: mErr } = await supabase
          .from('office_members')
          .select('office_id')
          .eq('user_id', userId);
        if (mErr) throw mErr;
        const ids = (members ?? []).map((m) => m.office_id);
        if (ids.length === 0) {
          setOffices([]);
          return;
        }
        const { data, error } = await supabase
          .from('offices')
          .select('*')
          .in('id', ids)
          .order('id', { ascending: true });
        if (error) throw error;
        setOffices((data ?? []) as Office[]);
      }
    } catch (e) {
      console.error('Failed to load offices', e);
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await refreshOffices();
      setLoading(false);
    })();

    const { data: sub } = supabase.auth.onAuthStateChange(() => {
      refreshOffices();
    });

    return () => {
      sub.subscription.unsubscribe();
    };
  }, [refreshOffices]);

  // Resolve a valid current office once the list has loaded.
  useEffect(() => {
    if (offices.length === 0) return;
    if (currentOfficeId && offices.some((o) => o.id === currentOfficeId)) return;
    const first = offices[0];
    setCurrentOfficeId(first.id);
    localStorage.setItem(STORAGE_KEY, String(first.id));
  }, [offices, currentOfficeId]);

  const currentOffice = offices.find((o) => o.id === currentOfficeId) ?? null;

  const switchOffice = useCallback((id: number) => {
    setCurrentOfficeId(id);
    localStorage.setItem(STORAGE_KEY, String(id));
  }, []);

  const createOffice = useCallback(
    async (name: string): Promise<Office | null> => {
      const trimmed = name.trim();
      if (!trimmed) return null;
      const { data, error } = await supabase
        .from('offices')
        .insert({ name: trimmed })
        .select('*')
        .single();
      if (error) {
        console.error('Failed to create office', error);
        return null;
      }
      const office = data as Office;
      setOffices((prev) => [...prev, office]);
      switchOffice(office.id);
      return office;
    },
    [switchOffice],
  );

  const renameOffice = useCallback(
    async (id: number, name: string): Promise<Office | null> => {
      const trimmed = name.trim();
      if (!trimmed) return null;
      const { data, error } = await supabase
        .from('offices')
        .update({ name: trimmed })
        .eq('id', id)
        .select('*')
        .single();
      if (error) {
        console.error('Failed to rename office', error);
        return null;
      }
      const office = data as Office;
      setOffices((prev) => prev.map((o) => (o.id === id ? office : o)));
      return office;
    },
    [],
  );

  const deleteOffice = useCallback(async (id: number): Promise<boolean> => {
    const { error } = await supabase.from('offices').delete().eq('id', id);
    if (error) {
      console.error('Failed to delete office', error);
      return false;
    }
    setOffices((prev) => prev.filter((o) => o.id !== id));
    return true;
  }, []);

  return (
    <OfficeContext.Provider
      value={{
        offices,
        currentOfficeId,
        currentOffice,
        loading,
        createOffice,
        renameOffice,
        deleteOffice,
        switchOffice,
        refreshOffices,
      }}
    >
      {children}
    </OfficeContext.Provider>
  );
}

export function useOffice() {
  const ctx = useContext(OfficeContext);
  if (!ctx) throw new Error('useOffice must be used within an OfficeProvider');
  return ctx;
}